// Frontend mirror of the backend pieces / m² / m³ conversion rule.
//
// MUST stay in sync with backend/src/utils/dimensionConversion.js — WZ/PZ
// presentation and supplier-order allocation preview use this mirror, while
// backend documents and allocation persistence use the backend twin. Allocation
// saves stay backend-authoritative. Dimensions are in millimetres
// (OrderProduct / ProductVariant schema). A "source" exposes
// width / length / height / thickness (mm) + an optional unit; quantity is the
// line quantity in its own selected unit. The plane is width×length when
// available; window/profile rows without length use width×height (or
// length×height when width is absent). A height used as a plane axis is never
// also treated as depth.

import { formatUnit } from './units';
import { THOUSANDS_GROUPING } from './numberFormatting';

export const ALLOCATION_UNITS = ['pcs', 'm2', 'm3'] as const;
export const WZ_CONVERSION_UNITS = ALLOCATION_UNITS;
export const ALLOCATION_QUANTITY_PRECISION = 3;
export type AllocationUnit = (typeof ALLOCATION_UNITS)[number];

export type AllocationConversionError =
  | 'INVALID_QUANTITY'
  | 'MISSING_DIMENSIONS'
  | 'UNSUPPORTED_CONVERSION';

export interface AllocationConversionResult {
  value: number | null;
  exactValue: number | null;
  error: AllocationConversionError | null;
}

export type WZConversionUnit = (typeof WZ_CONVERSION_UNITS)[number];

export interface ConversionSource {
  width?: number | null;
  length?: number | null;
  height?: number | null;
  thickness?: number | null;
  unit?: string | null;
  /**
   * „Liczymy tylko w sztukach” (Product."countInPiecesOnly", migration 196):
   * the dimensions are a description only — no m² / m³ is derived from them.
   */
  countInPiecesOnly?: boolean | null;
}

export function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeLinearDimensionMm(value: unknown): number | null {
  const numericValue = toNumber(value);
  if (!numericValue) return null;
  return numericValue;
}

export interface PlaneDimensionsMm {
  firstMm: number;
  secondMm: number;
  usesHeight: boolean;
}

export interface DimensionGeometryMm {
  plane: PlaneDimensionsMm | null;
  areaPerPieceM2: number | null;
  depthMm: number | null;
  volumePerPieceM3: number | null;
}

export function resolvePlaneDimensionsMm(source: ConversionSource = {}): PlaneDimensionsMm | null {
  const widthMm = normalizeLinearDimensionMm(source.width);
  const lengthMm = normalizeLinearDimensionMm(source.length);
  const heightMm = normalizeLinearDimensionMm(source.height);

  // The established board/product rule remains first: when width and length
  // exist, height is depth (and therefore takes precedence over thickness).
  if (widthMm && widthMm > 0 && lengthMm && lengthMm > 0) {
    return { firstMm: widthMm, secondMm: lengthMm, usesHeight: false };
  }

  // Windows commonly have width and height, but intentionally no length.
  if (widthMm && widthMm > 0 && heightMm && heightMm > 0) {
    return { firstMm: widthMm, secondMm: heightMm, usesHeight: true };
  }

  // Profiles may instead use length and height, with no width.
  if (lengthMm && lengthMm > 0 && heightMm && heightMm > 0) {
    return { firstMm: lengthMm, secondMm: heightMm, usesHeight: true };
  }

  return null;
}

export function resolveThirdDimensionMm(source: ConversionSource = {}): number | null {
  const plane = resolvePlaneDimensionsMm(source);
  const thicknessMm = normalizeLinearDimensionMm(source.thickness);

  // With width×height or length×height, height already defines m² and cannot
  // be reused as a made-up third axis. Only an explicit thickness completes
  // m³ in that geometry.
  if (plane?.usesHeight) {
    return thicknessMm && thicknessMm > 0 ? thicknessMm : null;
  }

  const heightMm = normalizeLinearDimensionMm(source.height);
  if (heightMm && heightMm > 0) return heightMm;

  return thicknessMm && thicknessMm > 0 ? thicknessMm : null;
}

/**
 * Stable geometry contract for every pcs/m²/m³ consumer.
 *
 * `areaPerPieceM2` is available only when two deterministic plane axes exist;
 * `depthMm` is available only when a third, non-reused axis exists. Consumers
 * such as transport and packaging must use these fields rather than repeating
 * width/length/height/thickness precedence locally.
 */
// Mirror of the backend NO_GEOMETRY seam (dimensionConversion.js).
const NO_GEOMETRY: DimensionGeometryMm = Object.freeze({
  plane: null, areaPerPieceM2: null, depthMm: null, volumePerPieceM3: null,
}) as DimensionGeometryMm;

export function resolveDimensionGeometryMm(source: ConversionSource = {}): DimensionGeometryMm {
  if (source?.countInPiecesOnly === true) return NO_GEOMETRY;
  const plane = resolvePlaneDimensionsMm(source);
  const depthMm = resolveThirdDimensionMm(source);

  return {
    plane,
    areaPerPieceM2: plane ? (plane.firstMm * plane.secondMm) / 1000000 : null,
    depthMm,
    volumePerPieceM3: plane && depthMm
      ? (plane.firstMm * plane.secondMm * depthMm) / 1000000000
      : null,
  };
}

// Exported so transportCostAllocation.ts can normalize the commercial unit
// the same way calculatePackagingLdm's backend twin does, which imports the
// same helper from backend/src/utils/dimensionConversion.js. No formula change.
export function normalizeQuantityUnit(unit?: string | null): string {
  const normalized = String(unit || 'pcs').trim().toLowerCase();
  if (['szt.', 'szt', 'piece', 'pieces'].includes(normalized)) {
    return 'pcs';
  }
  return normalized;
}

export function isActiveQuantityUnit(unit?: string | null): boolean {
  return (ALLOCATION_UNITS as readonly string[]).includes(normalizeQuantityUnit(unit));
}

export function calculateAreaM2(source: ConversionSource = {}, quantity: number = 1): number | null {
  const normalizedUnit = normalizeQuantityUnit(source.unit);
  const normalizedQuantity = Math.max(Number(quantity) || 0, 0);

  if (normalizedQuantity === 0) {
    return null;
  }

  // Historical kg/l/m/mb rows remain readable, but they are not part of the
  // active dimensional model and must never be silently interpreted as pcs.
  if (!isActiveQuantityUnit(normalizedUnit)) {
    return null;
  }

  if (normalizedUnit === 'm2') {
    return normalizedQuantity;
  }

  if (normalizedUnit === 'm3') {
    const { depthMm } = resolveDimensionGeometryMm(source);
    return depthMm ? normalizedQuantity / (depthMm / 1000) : null;
  }

  const { areaPerPieceM2 } = resolveDimensionGeometryMm(source);
  if (!areaPerPieceM2) {
    return null;
  }

  return areaPerPieceM2 * normalizedQuantity;
}

export function calculateVolumeM3(source: ConversionSource = {}, quantity: number = 1): number | null {
  const normalizedUnit = normalizeQuantityUnit(source.unit);
  const normalizedQuantity = Math.max(Number(quantity) || 0, 0);

  if (normalizedQuantity === 0) {
    return null;
  }

  // Historical kg/l/m/mb rows remain readable, but they are not part of the
  // active dimensional model and must never be silently interpreted as pcs.
  if (!isActiveQuantityUnit(normalizedUnit)) {
    return null;
  }

  if (normalizedUnit === 'm3') {
    return normalizedQuantity;
  }

  const geometry = resolveDimensionGeometryMm(source);
  if (!geometry.depthMm) {
    return null;
  }

  if (normalizedUnit === 'm2') {
    return normalizedQuantity * (geometry.depthMm / 1000);
  }

  if (!geometry.volumePerPieceM3) {
    return null;
  }

  return geometry.volumePerPieceM3 * normalizedQuantity;
}

export function normalizeConversionUnit(value: unknown): WZConversionUnit | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const normalized = String(value).trim().toLowerCase();
  return (WZ_CONVERSION_UNITS as readonly string[]).includes(normalized)
    ? (normalized as WZConversionUnit)
    : null;
}

function roundAllocationQuantityUp(value: number): number {
  const scale = 10 ** ALLOCATION_QUANTITY_PRECISION;
  return Number((Math.ceil((value * scale) - 1e-9) / scale).toFixed(ALLOCATION_QUANTITY_PRECISION));
}

/**
 * Convert an operator-entered allocation into the supplier-order item's unit.
 * Mirrors backend/src/utils/dimensionConversion.js exactly.
 */
export function convertAllocationQuantity({
  quantity,
  sourceUnit,
  targetUnit,
  width,
  length,
  height,
  thickness,
  countInPiecesOnly = false,
}: ConversionSource & {
  quantity: unknown;
  sourceUnit: string | null | undefined;
  targetUnit: string | null | undefined;
}): AllocationConversionResult {
  const normalizedQuantity = toNumber(quantity);
  const normalizedSourceUnit = normalizeQuantityUnit(sourceUnit);
  const normalizedTargetUnit = normalizeQuantityUnit(targetUnit);

  if (!normalizedQuantity || normalizedQuantity < 0) {
    return { value: null, exactValue: null, error: 'INVALID_QUANTITY' };
  }

  if (
    !(ALLOCATION_UNITS as readonly string[]).includes(normalizedSourceUnit)
    || !(ALLOCATION_UNITS as readonly string[]).includes(normalizedTargetUnit)
  ) {
    return { value: null, exactValue: null, error: 'UNSUPPORTED_CONVERSION' };
  }

  if (normalizedSourceUnit === normalizedTargetUnit) {
    return {
      value: roundAllocationQuantityUp(normalizedQuantity),
      exactValue: normalizedQuantity,
      error: null,
    };
  }

  const positiveDimension = (value: unknown): number | null => {
    const numericValue = toNumber(value);
    return numericValue && numericValue > 0 ? numericValue : null;
  };
  const dimensions: ConversionSource = {
    width: positiveDimension(width),
    length: positiveDimension(length),
    height: positiveDimension(height),
    thickness: positiveDimension(thickness),
    unit: 'pcs',
    countInPiecesOnly: countInPiecesOnly === true,
  };
  let convertedValue: number | null = null;

  if (normalizedSourceUnit === 'pcs' && normalizedTargetUnit === 'm2') {
    convertedValue = calculateAreaM2(dimensions, normalizedQuantity);
  } else if (normalizedSourceUnit === 'm2' && normalizedTargetUnit === 'pcs') {
    const areaPerPiece = calculateAreaM2(dimensions, 1);
    convertedValue = areaPerPiece ? normalizedQuantity / areaPerPiece : null;
  } else if (normalizedSourceUnit === 'pcs' && normalizedTargetUnit === 'm3') {
    convertedValue = calculateVolumeM3(dimensions, normalizedQuantity);
  } else if (normalizedSourceUnit === 'm3' && normalizedTargetUnit === 'pcs') {
    const volumePerPiece = calculateVolumeM3(dimensions, 1);
    convertedValue = volumePerPiece ? normalizedQuantity / volumePerPiece : null;
  } else if (normalizedSourceUnit === 'm2' && normalizedTargetUnit === 'm3') {
    convertedValue = calculateVolumeM3({ ...dimensions, unit: 'm2' }, normalizedQuantity);
  } else if (normalizedSourceUnit === 'm3' && normalizedTargetUnit === 'm2') {
    convertedValue = calculateAreaM2({ ...dimensions, unit: 'm3' }, normalizedQuantity);
  } else {
    return { value: null, exactValue: null, error: 'UNSUPPORTED_CONVERSION' };
  }

  if (!convertedValue) {
    return { value: null, exactValue: null, error: 'MISSING_DIMENSIONS' };
  }

  return {
    value: roundAllocationQuantityUp(convertedValue),
    exactValue: convertedValue,
    error: null,
  };
}

// Compute the converted value for a line, or null when it cannot be computed
// (no conversion chosen, or the product lacks the dimensions it needs).
export function computeConversionValue(
  unit: WZConversionUnit | null | undefined,
  source: ConversionSource,
  quantity: number,
): number | null {
  if (!unit) return null;

  const conversion = convertAllocationQuantity({
    ...source,
    quantity,
    sourceUnit: source.unit || 'pcs',
    targetUnit: unit,
  });
  return conversion.error ? null : conversion.exactValue;
}

// "31,25 m²" / "0,375 m³" / "12,5 szt." in Polish formatting, or '' when
// not computable. Unit labels stay owned by utils/units.ts.
export function formatConversionValue(
  unit: WZConversionUnit | null | undefined,
  source: ConversionSource,
  quantity: number,
): string {
  const value = computeConversionValue(unit, source, quantity);
  if (value === null || value === 0) {
    return '';
  }

  const fractionDigits = unit === 'm3' ? 3 : unit === 'm2' ? 2 : 0;
  const formatted = value.toLocaleString('pl-PL', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: unit === 'pcs' ? 3 : fractionDigits,
    ...THOUSANDS_GROUPING,
  } as Intl.NumberFormatOptions);
  return `${formatted} ${formatUnit(unit, 'pl')}`;
}
